import React, { useEffect, useState } from "react";

import { faCommentsAlt } from "@fortawesome/pro-duotone-svg-icons";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { toast } from "@performyard/py-components";
//utils
import { sanitizeHtmlContent, validateContentSecurity } from "@performyard/utils-isomorphic";

//cmoponents
import Button from "../../../../components/Button";
import IconButton from "../../../../components/Button/IconButton";
import Chip from "../../../../components/Chip";
import EmployeePicker, { SingleEmployee } from "../../../../components/EmployeePicker";
import Panel from "../../../../components/Panel";
import { TextEditor } from "../../../../components/TextEditor";

//api
import { useIsFeedbackRequestValid, useMutateSingleFeedbackRequest } from "../../../../api/hooks/defs/feedback";

//store
import { useRequestFeedbackDrawerStore } from "../../FeedbackDrawerStore";

const FEEDBACK_REQUEST_MAX_LENGTH = 560;

const FeedbackRequestDrawer = () => {
	const { closeDrawer, isOpen, requestData } = useRequestFeedbackDrawerStore();
	const [employees, setEmployees] = useState<SingleEmployee[]>([]);
	const [message, setMessage] = useState<string>("");
	const { mutate, isPending } = useMutateSingleFeedbackRequest({
		onSuccess: closeDrawer,
	});
	const { data: isValid, isLoading } = useIsFeedbackRequestValid(employees.map((v) => v.value));

	useEffect(() => {
		if (isOpen) {
			setEmployees([]);
		}
	}, [isOpen]);

	const handleSubmit = () => {
		const sanitizedMessage = sanitizeHtmlContent(message);

		const validation = validateContentSecurity(sanitizedMessage);
		if (!validation.isValid) {
			toast.error("Invalid feedback content");
			return;
		}

		if (requestData) {
			mutate({ id: requestData.requestId, message: sanitizedMessage });
		} else {
			mutate({ message: sanitizedMessage, requestedTo: employees.map((v) => v.value) });
		}
	};

	return (
		<Panel side="right" isOpen={isOpen} onClose={closeDrawer} width={480}>
			<div className="p-6">
				<h2
					data-testid="request-feedback-title"
					className="margin-auto max-w-full text-center text-[30px] font-[900] text-primary-5"
				>
					<FontAwesomeIcon icon={faCommentsAlt} /> REQUEST FEEDBACK
				</h2>

				{requestData?.requestedTo ? (
					<div className="mt-5">
						<span className="pb-1 text-paragraph-3 font-semi-bold">Requesting Feedback From:</span>
						<Chip avatarID={requestData?.requestedTo._id} label={requestData?.requestedTo.name} />
					</div>
				) : (
					<div id="user input" data-testid="user-input" className="mt-5">
						<span className="text-paragraph-3 font-semi-bold">
							Who would you like to request feedback from?
						</span>
						<EmployeePicker
							placeholder="Select an employee..."
							onChange={(value) => setEmployees((value as SingleEmployee[]) || [])}
							value={employees}
							isMultiSelect
							excludeCurrentUser
						/>
					</div>
				)}
				{isLoading || employees.length === 0 || isValid?.is_allowed ? null : (
					<span className="mt-2 text-caption text-red-5">
						One or more of the selected employees have already been requested in the past 14 days.
					</span>
				)}
				<div id="message" className="mb-6 mt-4">
					<span className="text-paragraph-3 font-semi-bold">Optional Message</span>
					<TextEditor
						key={"request-feedback-message"}
						initialValue={requestData?.message}
						onChange={(value) => setMessage(value)}
						className={"max-w-full"}
						maxLength={FEEDBACK_REQUEST_MAX_LENGTH}
						showCharacterCounter
						minHeight={200}
						autoFocus
					/>
				</div>
				{requestData || employees.length > 0 ? (
					<div className="fixed bottom-0 w-[465px] max-w-full bg-neutral-0">
						<Button
							isRequesting={isPending}
							disabled={
								isPending ||
								message.length > FEEDBACK_REQUEST_MAX_LENGTH ||
								(!requestData && !isValid?.is_allowed)
							}
							size="large"
							style={{ width: "calc(100% - 24px)", margin: 8 }}
							onClick={handleSubmit}
						>
							{requestData ? "Edit" : "Create"} Request
						</Button>
					</div>
				) : null}

				<IconButton
					testId="request-feedback-close-button"
					icon={faXmark}
					className="absolute right-2 top-2"
					onClick={closeDrawer}
				/>
			</div>
		</Panel>
	);
};

export default FeedbackRequestDrawer;
